import * as React from 'react';

import { FormField } from 'components/form';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import DatePickerMulti from 'components/date-picker/date-picker-multi/DatePickerMultiLazy';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';

import { __t, applyTranslations } from 'utils/language';
import { isFalsy, isNotEmpty } from 'utils/validators';
import {
  getCreatorNameWithTimelineOverlapForChange,
  isValidBulkChangeTimelineOrder
} from 'modules/subcampaigns/utils/bulk-offer-validation';

import type { SyntheticEvent } from 'react';
import type {
  BulkOfferModalSelectedTimelines,
  BulkOfferModalSubcampaignCustomOffer,
  BulkOfferModalTimelineData
} from 'modules/subcampaigns/types/BulkOfferModal.type';

import './bulk-timeline-form.scss';

applyTranslations(require('./bulk-timeline-form.i18n.json'));

type Props = {
  pickedTimelines: BulkOfferModalSelectedTimelines,
  contentTimelines: BulkOfferModalTimelineData,
  groupedDeliverables: Array<BulkOfferModalSubcampaignCustomOffer>,
  selectedDeliverableIds: Array<number>,
  isZalandoOutfitOnly: boolean,
  onTimelineSelect: (event: SyntheticEvent<HTMLInputElement>) => void,
  onDateChange: (
    type: 'contentApproval' | 'goLive' | 'insightsProvision',
    startDate?: string | null,
    endDate?: string | null
  ) => void
};

const BulkTimelineForm: React.FC<Props> = ({
  pickedTimelines,
  contentTimelines,
  groupedDeliverables,
  selectedDeliverableIds,
  isZalandoOutfitOnly,
  onTimelineSelect,
  onDateChange
}: Props) => {
  const isValidDateOrder = isValidBulkChangeTimelineOrder(pickedTimelines, contentTimelines);
  const erroredCreatorName = getCreatorNameWithTimelineOverlapForChange(
    groupedDeliverables,
    selectedDeliverableIds,
    pickedTimelines,
    contentTimelines
  );

  return (
    <div className="bulk-timeline-form">
      <FormField
        label={__t('bulk_timeline_form.label_apply_to')}
        labelType="highlighted"
        showValidationIcon={false}
        direction="row"
        fontSize="small"
        className="bulk-timeline-form__timeline-label"
        validators={[
          isNotEmpty(
            Object.values(pickedTimelines).some(picked => picked),
            __t('bulk_timeline_form.errors.none_selected')
          ),
          isFalsy(
            erroredCreatorName,
            __t('bulk_timeline_form.errors.check_creator', { erroredCreatorName })
          )
        ]}
      >
        <FormField
          showValidationIcon={false}
          validators={[
            isNotEmpty(
              !pickedTimelines.contentApproval || contentTimelines.contentApproval.startDate,
              __t('bulk_timeline_form.errors.missing_dates')
            ),
            isNotEmpty(
              !pickedTimelines.contentApproval || contentTimelines.contentApproval.endDate,
              __t('bulk_timeline_form.errors.missing_dates')
            ),
            isNotEmpty(
              !pickedTimelines.contentApproval || isValidDateOrder,
              __t('bulk_timeline_form.errors.content_approval_order')
            )
          ]}
        >
          <div className="bulk-timeline-form__timeline-input">
            <div className="bulk-timeline-form__timeline-checkbox">
              <InputCheckbox
                name="type"
                value="contentApproval"
                id="checkbox-filter--content-approval"
                label={__t('bulk_timeline_form.label_content_approval')}
                size="small"
                checked={pickedTimelines.contentApproval}
                onChange={onTimelineSelect}
              />
            </div>
            <DatePickerMulti
              startDateName="contentApprovalStartDate"
              startDate={contentTimelines.contentApproval.startDate}
              endDateName="contentApprovalEndDate"
              endDate={contentTimelines.contentApproval.endDate}
              onChange={(startDate, endDate) => onDateChange('contentApproval', startDate, endDate)}
            />
          </div>
        </FormField>

        {
          !isZalandoOutfitOnly && (
            <React.Fragment>
              <VerticalSpacing type="medium" />

              <FormField
                showValidationIcon={false}
                validators={[
                  isNotEmpty(
                    !pickedTimelines.goLive || contentTimelines.goLive.startDate,
                    __t('bulk_timeline_form.errors.missing_dates')
                  ),
                  isNotEmpty(
                    !pickedTimelines.goLive || contentTimelines.goLive.endDate,
                    __t('bulk_timeline_form.errors.missing_dates')
                  ),
                  isNotEmpty(
                    !pickedTimelines.goLive || isValidDateOrder,
                    __t('bulk_timeline_form.errors.go_live_order')
                  )
                ]}
              >
                <div className="bulk-timeline-form__timeline-input">
                  <div className="bulk-timeline-form__timeline-checkbox">
                    <InputCheckbox
                      name="type"
                      value="goLive"
                      label={__t('bulk_timeline_form.label_go_live')}
                      id="checkbox-filter--go-live"
                      size="small"
                      checked={pickedTimelines.goLive}
                      onChange={onTimelineSelect}
                    />
                  </div>
                  <DatePickerMulti
                    startDateName="goLiveStartDate"
                    startDate={contentTimelines.goLive.startDate}
                    endDateName="goLiveEndDate"
                    endDate={contentTimelines.goLive.endDate}
                    onChange={(startDate, endDate) => onDateChange('goLive', startDate, endDate)}
                  />
                </div>
              </FormField>

              <VerticalSpacing type="medium" />

              <FormField
                showValidationIcon={false}
                validators={[
                  isNotEmpty(
                    !pickedTimelines.insightsProvision || contentTimelines.insightsProvision.startDate,
                    __t('bulk_timeline_form.errors.missing_dates')
                  ),
                  isNotEmpty(
                    !pickedTimelines.insightsProvision || contentTimelines.insightsProvision.endDate,
                    __t('bulk_timeline_form.errors.missing_dates')
                  ),
                  isNotEmpty(!pickedTimelines.insightsProvision || isValidDateOrder,
                    __t('bulk_timeline_form.errors.insights_provision_order')
                  )
                ]}
              >
                <div className="bulk-timeline-form__timeline-input">
                  <div className="bulk-timeline-form__timeline-checkbox">
                    <InputCheckbox
                      name="type"
                      value="insightsProvision"
                      label={__t('bulk_timeline_form.label_insights')}
                      id="checkbox-filter--insights"
                      size="small"
                      checked={pickedTimelines.insightsProvision}
                      onChange={onTimelineSelect}
                    />
                  </div>
                  <DatePickerMulti
                    startDateName="insightsStartDate"
                    startDate={contentTimelines.insightsProvision.startDate}
                    endDateName="insightsEndDate"
                    endDate={contentTimelines.insightsProvision.endDate}
                    onChange={(startDate, endDate) => onDateChange('insightsProvision', startDate, endDate)}
                  />
                </div>
              </FormField>
            </React.Fragment>
          )
        }
      </FormField>
    </div>
  );
};

export default BulkTimelineForm;
